(() => {
  const SELECTOR = '[data-md-advice-drawer]';

  const debounce = (callback, wait = 250) => {
    let timeout;
    return (...args) => {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => callback(...args), wait);
    };
  };

  const escapeHtml = (value = '') => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const initDrawer = (drawer) => {
    if (!drawer || drawer.dataset.mdAdviceInitialized === 'true') return;
    drawer.dataset.mdAdviceInitialized = 'true';

    const panel = drawer.querySelector('.md-advice-drawer__panel');
    const closeButtons = drawer.querySelectorAll('[data-md-advice-close]');
    const backButton = drawer.querySelector('[data-md-advice-back]');
    const screens = drawer.querySelectorAll('[data-md-advice-screen]');
    const productSearch = drawer.querySelector('[data-md-advice-product-search]');
    const productResults = drawer.querySelector('[data-md-advice-product-results]');
    const selectedProduct = drawer.querySelector('[data-md-advice-selected-product]');
    const selectedProductImage = drawer.querySelector('[data-md-advice-selected-product-image]');
    const selectedProductTitle = drawer.querySelector('[data-md-advice-selected-product-title]');
    const removeProductButton = drawer.querySelector('[data-md-advice-product-remove]');
    const productTitleInput = drawer.querySelector('[data-md-advice-product-title-input]');
    const productUrlInput = drawer.querySelector('[data-md-advice-product-url-input]');
    const productIdInput = drawer.querySelector('[data-md-advice-product-id-input]');
    const productHelp = drawer.querySelector('[data-md-advice-product-help]');
    const successMarker = drawer.querySelector('[data-md-advice-success-marker]');
    const errorMarker = drawer.querySelector('[data-md-advice-error-marker]');
    const predictiveUrl = drawer.dataset.predictiveUrl;
    const minimumSearchLength = Number(drawer.dataset.searchMin || 2);

    let activeScreen = 'home';
    let lastFocusedElement = null;
    let abortController = null;

    const getTriggers = () => document.querySelectorAll('[data-md-advice-open]');

    const setScreen = (name) => {
      activeScreen = name;

      screens.forEach((screen) => {
        const isActive = screen.dataset.mdAdviceScreen === name;
        screen.hidden = !isActive;
        screen.classList.toggle('is-active', isActive);
      });

      if (backButton) {
        backButton.hidden = name === 'home' || name === 'success';
      }

      drawer.querySelector('.md-advice-drawer__scroll')?.scrollTo({ top: 0, behavior: 'auto' });
    };

    const closeResults = () => {
      if (!productResults || !productSearch) return;
      productResults.hidden = true;
      productSearch.setAttribute('aria-expanded', 'false');
    };

    const openDrawer = (screen = 'home', trigger = null) => {
      const mobileMenu = trigger?.closest?.('[data-md-mobile-menu]');
      const header = trigger?.closest?.('[data-section-id]');
      const mobileToggle = header?.querySelector?.('[data-md-menu-toggle]');

      if (mobileMenu?.classList.contains('is-open')) {
        mobileMenu.classList.remove('is-open');
        mobileMenu.setAttribute('aria-hidden', 'true');
        mobileMenu.setAttribute('inert', '');
        mobileToggle?.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('md-menu-open');
        lastFocusedElement = mobileToggle || trigger || document.activeElement;
      } else {
        lastFocusedElement = trigger || document.activeElement;
      }

      setScreen(screen);
      closeResults();

      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('md-advice-open');

      getTriggers().forEach((item) => item.setAttribute('aria-expanded', 'true'));

      window.requestAnimationFrame(() => {
        const target = screen === 'product'
          ? drawer.querySelector('[data-md-advice-screen="product"] select, [data-md-advice-screen="product"] input, [data-md-advice-screen="product"] textarea')
          : panel;
        target?.focus({ preventScroll: true });
      });
    };

    const closeDrawer = () => {
      closeResults();
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('md-advice-open');
      getTriggers().forEach((item) => item.setAttribute('aria-expanded', 'false'));

      if (lastFocusedElement instanceof HTMLElement) {
        lastFocusedElement.focus({ preventScroll: true });
      }
    };

    const clearSelectedProduct = () => {
      if (selectedProduct) {
        selectedProduct.hidden = true;
        selectedProduct.classList.add('is-empty');
      }
      if (selectedProductImage) selectedProductImage.innerHTML = '';
      if (selectedProductTitle) selectedProductTitle.textContent = '';
      if (productTitleInput) productTitleInput.value = '';
      if (productUrlInput) productUrlInput.value = '';
      if (productIdInput) productIdInput.value = '';
      if (productHelp) productHelp.hidden = false;
    };

    const selectProduct = (product) => {
      if (!product || !selectedProduct) return;

      const imageUrl = product.featured_image?.url || product.image || '';
      const imageAlt = product.featured_image?.alt || product.title || '';

      if (selectedProductImage) {
        selectedProductImage.innerHTML = imageUrl
          ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(imageAlt)}" width="54" height="54" loading="lazy">`
          : '';
      }

      if (selectedProductTitle) selectedProductTitle.textContent = product.title || '';
      if (productTitleInput) productTitleInput.value = product.title || '';
      if (productUrlInput) {
        try {
          productUrlInput.value = new URL(product.url, window.location.origin).href;
        } catch {
          productUrlInput.value = product.url || '';
        }
      }
      if (productIdInput) productIdInput.value = product.id || '';

      selectedProduct.hidden = false;
      selectedProduct.classList.remove('is-empty');
      if (productHelp) productHelp.hidden = true;
      if (productSearch) productSearch.value = '';
      closeResults();
    };

    const renderResults = (products = [], message = '') => {
      if (!productResults || !productSearch) return;

      if (message) {
        productResults.innerHTML = `<div class="md-advice-drawer__product-result-empty">${escapeHtml(message)}</div>`;
      } else {
        productResults.innerHTML = products.map((product) => {
          const imageUrl = product.featured_image?.url || product.image || '';
          const imageAlt = product.featured_image?.alt || product.title || '';

          return `
            <button
              type="button"
              class="md-advice-drawer__product-result"
              role="option"
              data-md-advice-product-result
              data-product-id="${escapeHtml(product.id)}"
            >
              <span class="md-advice-drawer__product-result-image">
                ${imageUrl ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(imageAlt)}" width="46" height="46" loading="lazy">` : ''}
              </span>
              <strong>${escapeHtml(product.title)}</strong>
            </button>`;
        }).join('');

        productResults.querySelectorAll('[data-md-advice-product-result]').forEach((button, index) => {
          button.addEventListener('click', () => selectProduct(products[index]));
        });
      }

      productResults.hidden = false;
      productSearch.setAttribute('aria-expanded', 'true');
    };

    const searchProducts = debounce(async () => {
      if (!productSearch || !productResults || !predictiveUrl) return;
      const query = productSearch.value.trim();

      if (query.length < minimumSearchLength) {
        closeResults();
        return;
      }

      abortController?.abort();
      abortController = new AbortController();

      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,variants.title,variants.sku,vendor'
      });

      try {
        const response = await fetch(`${predictiveUrl}?${params.toString()}`, {
          headers: { Accept: 'application/json' },
          signal: abortController.signal
        });

        if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);

        const payload = await response.json();
        const products = payload?.resources?.results?.products || [];

        renderResults(products, products.length ? '' : drawer.dataset.noResults);
      } catch (error) {
        if (error.name === 'AbortError') return;
        renderResults([], drawer.dataset.searchError);
      }
    }, 250);

    getTriggers().forEach((trigger) => {
      trigger.setAttribute('aria-controls', drawer.id);
      trigger.setAttribute('aria-expanded', 'false');
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        openDrawer('home', trigger);
      });
    });

    drawer.querySelectorAll('[data-md-advice-screen-target]').forEach((button) => {
      button.addEventListener('click', () => {
        setScreen(button.dataset.mdAdviceScreenTarget);
        window.requestAnimationFrame(() => {
          drawer.querySelector('[data-md-advice-screen="product"] select')?.focus({ preventScroll: true });
        });
      });
    });

    closeButtons.forEach((button) => button.addEventListener('click', closeDrawer));
    backButton?.addEventListener('click', () => setScreen('home'));
    removeProductButton?.addEventListener('click', clearSelectedProduct);

    productSearch?.addEventListener('input', searchProducts);
    productSearch?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !productResults?.hidden) {
        event.stopPropagation();
        closeResults();
      }
    });

    document.addEventListener('click', (event) => {
      if (!drawer.classList.contains('is-open')) return;
      if (productResults && productSearch && !productResults.hidden) {
        const productField = productSearch.closest('.md-advice-drawer__product-field');
        if (productField && !productField.contains(event.target)) closeResults();
      }
    });

    drawer.addEventListener('keydown', (event) => {
      if (!drawer.classList.contains('is-open')) return;

      if (event.key === 'Escape') {
        closeDrawer();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = [...drawer.querySelectorAll(
        'a[href], button:not([disabled]):not([hidden]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )].filter((element) => element.offsetParent !== null);

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    if (successMarker) {
      openDrawer('success');
    } else if (errorMarker) {
      openDrawer('product');
    }
  };

  const initAll = (root = document) => {
    root.querySelectorAll(SELECTOR).forEach(initDrawer);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
